<template>
  <div v-loading="programsFetchInProgress" class="program-list-table">
    <div class="row">
      <div class="col-sm-12 col-md-12">
        <p
          v-show="!programs.length && !programsFetchInProgress"
          class="text-muted"
        >
          There are no programs yet.
        </p>

        <table class="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Pending</th>
              <th>Submitted</th>
              <th>Is active?</th>
              <th class="text-center">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="program in programs" :key="program.id">
              <td>
                <router-link :to="routeForProgramResults(program.id)">
                  {{ program.name }}
                </router-link>
              </td>
              <td>
                <span class="label label-outline">
                  {{ getProgramDraftsCount(program) }} drafts
                </span>
              </td>
              <td>
                <span
                  v-if="!program.applications.answered"
                  class="label label-outline"
                  >0 applications</span
                >
                <router-link
                  v-if="program.applications.answered"
                  :to="routeForProgramResults(program.id)"
                  :class="{
                    'label-info has-answers': program.applications.answered > 0,
                  }"
                  class="label label-outline label-results"
                >
                  {{ program.applications.answered }} applications
                </router-link>
              </td>
              <td class="is-active-col">
                <span v-if="program.is_active" class="label label-success"
                  >Active</span
                >
                <span v-if="!program.is_active" class="label label-outline"
                  >Inactive</span
                >
              </td>
              <td>
                <div class="buttons">
                  <router-link
                    class="btn btn-info btn-xs btn-transparent"
                    :to="routeForProgramEdit(program.id)"
                  >
                    Edit
                  </router-link>
                  <button
                    class="btn btn-secondary btn-xs btn-transparent"
                    :disabled="isProgramBeingActivated(program.id)"
                    @click.prevent="toggle(program)"
                  >
                    <loader
                      v-if="isProgramBeingActivated(program.id)"
                      class="loader-xs is-black"
                    />
                    <span v-if="program.is_active">Deactivate</span>
                    <span v-if="!program.is_active">Activate</span>
                  </button>
                  <button
                    class="btn btn-secondary btn-xs btn-transparent"
                    :disabled="isProgramBeingCloned(program.id)"
                    @click.prevent="openCloneModal(program)"
                  >
                    <loader
                      v-if="isProgramBeingCloned(program.id)"
                      class="loader-xs is-black"
                    />
                    <span>Copy</span>
                  </button>
                  <button
                    class="btn btn-secondary btn-xs btn-transparent"
                    :disabled="program.is_active"
                    @click.prevent="remove(program)"
                  >
                    Remove
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <hr />

        <modal v-model="addNewProgramModal" title="Add new program">
          <el-form :model="addProgramForm">
            <el-form-item label="Name">
              <el-input
                ref="addProgramFormName"
                v-model="addProgramForm.name"
                placeholder="Program name"
                @keydown.enter.native.prevent="addProgram"
              />
            </el-form-item>
          </el-form>

          <div class="flex justify-end">
            <div class="relative">
              <span
                v-if="isProgramBeingAdded"
                class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70"
              >
                <loader class="loader-xs is-black" />
              </span>
              <el-button
                :disabled="isProgramBeingAdded"
                @click.stop.prevent="closeAddProgramModal"
              >
                Cancel
              </el-button>
              <el-button
                type="primary"
                :disabled="isProgramBeingAdded"
                @click.stop.prevent="addProgram"
              >
                Add
              </el-button>
            </div>
          </div>
        </modal>

        <button
          class="btn btn-primary"
          :disabled="isProgramBeingAdded"
          @click.prevent="openAddModal"
        >
          Add program
        </button>
      </div>
    </div>

    <modal v-model="showCloneModal" title="Clone program">
      <p>Are you sure you want to copy this program?</p>
      <el-form :model="cloneProgramForm">
        <el-form-item label="New program name">
          <el-input
            ref="cloneProgramFormName"
            v-model="cloneProgramForm.name"
          />
        </el-form-item>
      </el-form>
      <div class="flex justify-end">
        <div class="relative">
          <span
            v-if="isProgramBeingCloned(cloneProgramForm.id)"
            class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70"
          >
            <loader class="loader-xs is-black" />
          </span>
          <el-button
            :disabled="isProgramBeingCloned(cloneProgramForm.id)"
            @click.stop.prevent="showCloneModal = false"
          >
            Cancel
          </el-button>
          <el-button
            type="primary"
            :disabled="isProgramBeingCloned(cloneProgramForm.id)"
            @click.stop.prevent="cloneProgram"
          >
            Copy
          </el-button>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { notify } from '@/services/notifications'
import Modal from 'components/common/Modal.vue'

export default {
  components: {
    Modal,
  },
  props: {
    organizationId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      realOrganizationId: null,
      addNewProgramModal: false,
      showCloneModal: false,
      addProgramForm: {
        name: '',
      },
      cloneProgramForm: {
        name: '',
      },
    }
  },
  computed: {
    ...mapGetters({
      isProgramBeingActivated:
        'organizationProgramList/isProgramBeingActivated',
      isProgramBeingCloned: 'organizationProgramList/isProgramBeingCloned',
    }),
    ...mapState({
      user: (state) => state.user.data,
      programs: (state) => state.organizationProgramList.programs,
      programsFetchInProgress: (state) =>
        state.organizationProgramList.fetchInProgress,
      isProgramBeingAdded: (state) =>
        state.organizationProgramList.programBeingAdded,
    }),
    atAdmin() {
      return this.$route.name === 'adminOrganizationProgramList'
    },
  },
  async created() {
    console.log(' => ProgramListPage created')
    this.realOrganizationId = this.organizationId
    if (!this.realOrganizationId) {
      const user = await this.$store.dispatch('user/getUser')
      this.realOrganizationId = user.organizationId
    }
    this.fetchPrograms()
  },

  methods: {
    openAddNewProgramModal() {
      console.log(' => openAddNewProgramModal')
    },
    fetchPrograms() {
      this.$store.dispatch('organizationProgramList/fetch', {
        organizationId: this.realOrganizationId,
      })
    },
    async toggle(program) {
      const command = program.is_active ? 'deactivate' : 'activate'
      this.$store.dispatch(`organizationProgramList/${command}`, {
        organizationId: this.realOrganizationId,
        programId: program.id,
      })
    },
    openCloneModal(program) {
      this.showCloneModal = true
      setTimeout(() => {
        this.$refs.cloneProgramFormName.focus()
      }, 20)
      this.cloneProgramForm.id = program.id
      this.cloneProgramForm.name = `${program.name} - copy`
    },
    closeAddProgramModal() {
      this.addProgramForm.name = ''
      this.addNewProgramModal = false
    },
    openAddModal() {
      this.addNewProgramModal = true
      setTimeout(() => {
        this.$refs.addProgramFormName.focus()
      }, 20)
    },
    async addProgram() {
      await this.$store.dispatch(`organizationProgramList/add`, {
        organizationId: this.realOrganizationId,
        programName: this.addProgramForm.name,
      })
      this.fetchPrograms()
      notify({ type: 'success', title: 'Program added' })
      this.closeAddProgramModal()
    },
    async cloneProgram() {
      await this.$store.dispatch(`organizationProgramList/clone`, {
        organizationId: this.realOrganizationId,
        programId: this.cloneProgramForm.id,
        programName: this.cloneProgramForm.name,
      })
      this.fetchPrograms()
      this.showCloneModal = false
    },
    routeForProgramResults(programId) {
      let name = 'dashboardOrganizationProgramResults'
      if (this.atAdmin) {
        name = 'adminOrganizationProgramResults'
      }
      return { name, params: { programId } }
    },
    routeForProgramEdit(programId) {
      let name = 'dashboardOrganizationProgramEdit'
      if (this.atAdmin) {
        name = 'adminOrganizationProgramEdit'
      }
      return {
        name,
        params: { programId, organizationId: this.realOrganizationId },
      }
    },
    remove(program) {
      const msg = `Are you sure you want to remove program <b>${program.name}</b>?`
      this.$confirm(msg, 'Yes, remove it', { dangerouslyUseHTMLString: true })
        .then(() => {
          this.$store.dispatch('organizationProgramList/remove', {
            organizationId: this.realOrganizationId,
            programId: program.id,
            removeImmediately: true,
          })
          this.$message.success('Program has been successfully removed')
        })
        .catch(() => {})
    },
    getProgramDraftsCount(program) {
      return program.applications.total - program.applications.answered
    },
  },
}
</script>

<style lang="scss">
@import '~assets/css/colors.scss';

.program-list-table {
  .label-results {
    &.has-answers {
      cursor: pointer;
      &:hover {
        border-color: $purple60;
        background-color: $purple;
        color: $white;
      }
    }
  }

  .is-active-col {
    .label {
      display: inline-block;
      width: 80px;
      padding: 5px 12px;
      &.label-outline {
        padding: 3px 10px;
      }
    }
  }

  .buttons {
    display: flex;
    justify-content: space-around;

    .btn {
      margin: 0 2px;
    }
  }
}
</style>
